import React, { createContext, useContext, useEffect } from 'react';
import { useBaseSocket } from './BaseSocketContext';
import { useIntent } from './IntentContext';
import { toast } from 'react-toastify';

const IntentSocketContext = createContext();

export const useIntentSocket = () => {
    const context = useContext(IntentSocketContext);
    if (!context) {
        throw new Error('useIntentSocket must be used within IntentSocketProvider');
    }
    return context;
};

export const IntentSocketProvider = ({ children }) => {
    const { socket, connected } = useBaseSocket();
    const { updateRuleStats, fetchRules, fetchStats, fetchLogs } = useIntent();

    useEffect(() => {
        if (!socket || !connected) return;

        // Listen for intent detection events
        const handleIntentDetection = (data) => {
            const { ruleId, ruleName, success, confidence } = data;
            
            // Update rule stats in real-time
            if (ruleId) {
                updateRuleStats(ruleId, { success, confidence });
            }

            // Show success notification (optional - can be configured)
            if (success && window.location.pathname.includes('/intent')) {
                toast.success(
                    `Intent "${ruleName}" detected with ${Math.round(confidence * 100)}% confidence`,
                    { 
                        position: "top-right",
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    }
                );
            }
        };

        // Listen for rule status changes
        const handleRuleUpdated = (data) => {
            const { ruleName, isActive, updatedBy } = data;
            
            // Refresh rules list to get updated data
            fetchRules();
            
            // Show notification about rule changes
            if (window.location.pathname.includes('/intent')) {
                const action = isActive ? 'activated' : 'deactivated';
                toast.info(
                    `Intent rule "${ruleName}" was ${action}${updatedBy ? ` by ${updatedBy}` : ''}`,
                    {
                        position: "top-right",
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    }
                );
            }
        };

        // Listen for rule creation/deletion events
        const handleRuleCreated = (data) => {
            const { ruleName, createdBy } = data;
            
            // Refresh rules list
            fetchRules();
            
            if (window.location.pathname.includes('/intent')) {
                toast.success(
                    `New intent rule "${ruleName}" created${createdBy ? ` by ${createdBy}` : ''}`,
                    {
                        position: "top-right",
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    }
                );
            }
        };

        const handleRuleDeleted = (data) => {
            const { ruleName, deletedBy } = data;
            
            // Refresh rules list
            fetchRules();
            
            if (window.location.pathname.includes('/intent')) {
                toast.info(
                    `Intent rule "${ruleName}" was deleted${deletedBy ? ` by ${deletedBy}` : ''}`,
                    {
                        position: "top-right",
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    }
                );
            }
        };

        // Listen for system events
        const handleSystemStatus = (data) => {
            const { status, message } = data;
            
            if (window.location.pathname.includes('/intent')) {
                const toastType = status === 'enabled' || status === 'online' ? 'success' : 'warning';
                toast[toastType](
                    message || `Intent detection system ${status}`,
                    {
                        position: "top-right",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    }
                );
            }
        };

        // Listen for performance updates (for analytics page)
        const handlePerformanceUpdate = () => {
            // Refresh analytics data if user is on analytics page
            if (window.location.pathname.includes('/intent/analytics')) {
                fetchStats();
                fetchLogs({ page: 1, limit: 50 });
            }
        };

        // Register event listeners
        socket.on('intent:detection', handleIntentDetection);
        socket.on('intent:rule_updated', handleRuleUpdated);
        socket.on('intent:rule_created', handleRuleCreated);
        socket.on('intent:rule_deleted', handleRuleDeleted);
        socket.on('intent:system_status', handleSystemStatus);
        socket.on('intent:performance_update', handlePerformanceUpdate);

        // Cleanup listeners on unmount or when socket changes
        return () => {
            socket.off('intent:detection', handleIntentDetection);
            socket.off('intent:rule_updated', handleRuleUpdated);
            socket.off('intent:rule_created', handleRuleCreated);
            socket.off('intent:rule_deleted', handleRuleDeleted);
            socket.off('intent:system_status', handleSystemStatus);
            socket.off('intent:performance_update', handlePerformanceUpdate);
        };
    }, [socket, connected, updateRuleStats, fetchRules, fetchStats, fetchLogs]);

    // Emit events to server (optional - for bidirectional communication)
    const emitTestRule = (ruleId, message) => {
        if (socket && connected) {
            socket.emit('intent:test_rule', { ruleId, message });
        }
    };

    const emitRuleStatusChange = (ruleId, isActive) => {
        if (socket && connected) {
            socket.emit('intent:rule_status_change', { ruleId, isActive });
        }
    };

    const contextValue = {
        connected,
        emitTestRule,
        emitRuleStatusChange
    };

    return (
        <IntentSocketContext.Provider value={contextValue}>
            {children}
        </IntentSocketContext.Provider>
    );
};

export default IntentSocketContext;